// src/lib/storeKeys.js
// ─────────────────────────────────────────────────────────────────────────────
//  Canonical IDB / Firestore key constants.
//
//  RULE: Every useSyncStore(key) call in the codebase must use one of these
//  constants — never a bare string literal. A typo in a bare string silently
//  creates an orphaned IDB key and a ghost Firestore document with no runtime
//  error.
//
//  HOW TO ADD A KEY:
//    1. Add the constant here (alphabetical within its section).
//    2. Import it in the hook / file that calls useSyncStore().
//    3. Do not add the string directly to useSyncStore() — always go through this file.
// ─────────────────────────────────────────────────────────────────────────────

// ── Query limits ──────────────────────────────────────────────────────────────
/**
 * Default limit for all Firestore collection queries.
 * RULE: Every query(collection(...)) call must include limit(QUERY_LIMIT) or
 * a feature-specific cap (e.g. MAX_PHOTOS in Stewardship). Unbounded queries
 * scan every document — 1 query on 1,000 docs = 1,000 reads against the
 * 50k/day Spark quota.
 */
export const QUERY_LIMIT = 50;

// ── Journal / Daily ───────────────────────────────────────────────────────────
export const STORE_DAILY_ENTRIES           = 'dailyEntries';
export const STORE_WEEKLY_REPORTS          = 'weeklyReports';
export const STORE_MONTHLY_REPORTS         = 'monthlyReports';

// ── People & Contacts ─────────────────────────────────────────────────────────
export const STORE_PEOPLE_CONTACTS         = 'peopleContacts';

// ── Discipleship ──────────────────────────────────────────────────────────────
/** Tree of disciples (People added via DiscipleshipKeeper). */
export const STORE_DISCIPLESHIP_DATA       = 'discipleshipData';
/** Attendance records logged per discipleship session. */
export const STORE_DISCIPLESHIP_ATTENDANCE = 'discipleshipAttendance';

// ── Finances ──────────────────────────────────────────────────────────────────
export const STORE_EXPENSES                = 'expenses';
export const STORE_TITHES_OFFERING         = 'tithesOffering';
/** Outgoing missionary / ministry support commitments. */
export const STORE_OUTGOING_SUPPORT        = 'outgoingSupport';

// ── Sermons & Bible Study ─────────────────────────────────────────────────────
export const STORE_SERMON_NOTES            = 'sermonNotes';
/** Per-study progress objects keyed by study templateId. */
export const STORE_BIBLE_STUDY_PROGRESS    = 'bibleStudyProgress';

// ── Prayer ────────────────────────────────────────────────────────────────────
export const STORE_PRAYER_REQUESTS         = 'prayerRequests';
/** Daily check-in map: { 'YYYY-MM-DD': { category: true, ... } } */
export const STORE_PRAYER_CHECKINS         = 'prayerCheckins';

// ── Growth / Challenges ───────────────────────────────────────────────────────
/** Active (in-progress) challenges. */
export const STORE_CHALLENGES              = 'challenges';
/** Completed challenge records. */
export const STORE_COMPLETED_CHALLENGES    = 'completedChallenges';

// ── Calendar ──────────────────────────────────────────────────────────────────
export const STORE_CALENDAR_EVENTS         = 'calendarEvents';

// ── Dashboard / Habit Flags ───────────────────────────────────────────────────
/**
 * Ephemeral daily flags written by automation handlers to signal that an
 * activity has already happened today (e.g. { evangelismToday: 'YYYY-MM-DD' }).
 * Dashboard reads these to pre-tick the corresponding habit checkbox.
 */
export const STORE_HABIT_FLAGS             = 'habitFlags';

// ── Bible ─────────────────────────────────────────────────────────────────────
/**
 * Clipboard of highlighted verses — capped at 20 entries (FIFO).
 * Shape: Array<{ id, reference, text, date }>.
 */
export const STORE_VERSE_CLIPBOARD         = 'verseClipboard';

// ── Bible reader preferences & state ─────────────────────────────────────────
/** User's saved bookmarks: { [bookChapter]: [verse, ...] } */
export const STORE_BIBLE_BOOKMARKS         = 'bibleBookmarks';
/** Highlighted verse ranges: { [ref]: { color, note } } */
export const STORE_BIBLE_HIGHLIGHTS        = 'bibleHighlights';
/** Per-verse notes: { [ref]: string } */
export const STORE_BIBLE_NOTES             = 'bibleNotes';
/** Recently visited chapters: Array<{ book, chapter, ts }> */
export const STORE_BIBLE_HISTORY           = 'bibleHistory';
/** Completed-chapter ledger for the reading plan: object { 'Book N': 'YYYY-MM-DD' } (first read of each chapter). Pass {} as the initial value; with [] useSyncStore coerces it to an array of dates (useGlobalChallengeSync relies on this). */
export const STORE_BIBLE_READING_HISTORY   = 'bibleReadingHistory';
/** Font size setting for the Bible reader: 'sm' | 'md' | 'lg' | 'xl' */
export const STORE_BIBLE_FONT_SIZE         = 'bibleFontSize';
/** Whether to highlight Jesus's words in red: boolean */
export const STORE_BIBLE_RED_LETTER        = 'bibleRedLetter';
/** Whether to show the daily verse banner in the Bible reader: boolean */
export const STORE_BIBLE_SHOW_DAILY        = 'bibleShowDaily';
/** Selected Bible translation in the reader: 'kjv' | 'bsb' */
export const STORE_BIBLE_TRANSLATION       = 'bibleTranslation';

// ── Bible Study attendance ────────────────────────────────────────────────────
/**
 * Attendance records for Bible Study Guide sessions.
 * Distinct from STORE_DISCIPLESHIP_ATTENDANCE (general meetings).
 * Also add this key to Layout.BACKUP_KEYS to include it in auto-export.
 */
export const STORE_BIBLE_STUDY_ATTENDANCE  = 'bibleStudyAttendance';

// ── Stewardship / Finances ────────────────────────────────────────────────────
/** One-time gifts received (alias: legacy key 'giftList' maps here on restore). */
export const STORE_ONE_TIME_GIFTS          = 'oneTimeGifts';
/**
 * Monthly support received from sending churches / partners.
 * Shape: Array<{ id, name, amount, currency, date, ... }>.
 */
export const STORE_SUPPORT_RECEIVED        = 'supportReceived';
/**
 * Budget target and currency settings set in ExpenseLedger.
 * Shape: { monthlyPhp: number, monthlyUsd: number, currency: 'PHP'|'USD' }.
 */
export const STORE_BUDGET_SETTINGS         = 'budgetSettings';

// ── Reading Plan ──────────────────────────────────────────────────────────────
/**
 * Reading plan data for unauthenticated / pre-login users.
 * Used as a fallback key before uid is available.
 * Uid-keyed variant is: `readingPlan_${uid}` (dynamic — not a constant).
 */
export const STORE_READING_PLAN_ANON = 'readingPlan_anon';

// ── Profile ───────────────────────────────────────────────────────────────────
/**
 * User profile object — name, photo, lifeSeason, role, etc.
 * Also used by useAvatarProfile (Layout) for displayName and photoURL.
 */
export const STORE_USER_PROFILE = 'userProfile';
/**
 * Saved PDF files — stored as base64 strings.
 * IDB-only: large binary payload would exceed Firestore's 1 MB document limit.
 * Listed explicitly in syncManager.IDB_ONLY_KEYS.
 */
export const STORE_SAVED_PDFS              = 'savedPdfs';

// ── UI / Theme Settings ──────────────────────────────────────────────────────
/** Theme mode: 'light' | 'dark' | 'forest' */
export const STORE_THEME_MODE              = 'themeMode';
/** Font size: 'sm' | 'md' | 'lg' | 'xl' */
export const STORE_FONT_SIZE               = 'fontSize';
/** Platform mode: 'auto' | 'ios' | 'android' */
export const STORE_PLATFORM_MODE           = 'platformMode';
/** UI scale: 'compact' | 'default' | 'comfortable' */
export const STORE_UI_SCALE                = 'uiScale';
/** Line spacing: 'tight' | 'normal' | 'relaxed' */
export const STORE_LINE_SPACING            = 'lineSpacing';
/** Button scale: 'small' | 'default' | 'large' */
export const STORE_BUTTON_SCALE            = 'buttonScale';

// ── Feature Unlock / Progression ─────────────────────────────────────────────
/**
 * Persisted unlock level (0-5) for the progression system.
 * Tracks the highest level earned/master-unlocked so it survives re-renders.
 * Shape: number (0–5).
 */
export const STORE_FEATURE_UNLOCK_LEVEL    = 'featureUnlockLevel';
/**
 * Array of route strings that have been explicitly master-unlocked via
 * a mastery code, independent of the organic level progression.
 * Shape: string[] (e.g. ['/stewardship', '/evangelism']).
 */
export const STORE_FEATURE_MASTER_ROUTES   = 'featureMasterRoutes';

// ── Trash Bin ─────────────────────────────────────────────────────────────────
/**
 * Trash bin for deleted items — items are kept for 30 days before permanent deletion.
 * Shape: { [originalKey]: { data: any, deletedAt: string, expiresAt: string } }
 */
export const STORE_TRASH_BIN               = 'trashBin';

// ── Memory Verse ──────────────────────────────────────────────────────────────
/** Verses being memorized. Shape: Array<{ id, reference, text, box, nextReview, ... }>. */
export const STORE_MEMORY_VERSES           = 'memoryVerses';
/** Per-device reminder + popup settings (IDB-only, see syncManager.IDB_ONLY_KEYS). */
export const STORE_MEMORY_VERSE_SETTINGS   = 'memoryVerseSettings';
/** Days on which the user did a recall attempt. Shape: string[] of 'YYYY-MM-DD'. Feeds the 30-Day Memory Verse challenge. */
export const STORE_MEMORY_VERSE_DAYS       = 'memoryVerseDays';